/**
 * <custom-select> — accessible single-select dropdown.
 *
 * Implements the WAI-ARIA "Select-Only Combobox" (collapsible listbox) pattern:
 *   - The trigger is a <button role="combobox"> and keyboard focus NEVER leaves it
 *     (it only leaves on Tab / Shift+Tab).
 *   - The listbox is a <ul role="listbox"> of <li role="option"> elements, each with a unique id.
 *   - The highlighted option is exposed via aria-activedescendant on the trigger.
 *   - The highlighted option carries aria-selected="true"; it is removed from all others.
 *
 * Keyboard (matches the reference article):
 *   - Space / Enter ....... toggle the listbox (selection follows traversal, so no explicit commit)
 *   - Up / Down ........... select previous / next option; when collapsed the value cycles
 *                          without opening the listbox
 *   - Escape .............. collapse the listbox
 *   - Tab / Shift+Tab ..... move focus away (and close the listbox)
 *
 * Form integration: the selected value is mirrored into a hidden <input name="option{n}">,
 * and a bubbling "change" event is dispatched so the surrounding <variant-picker> updates the
 * variant. External value changes (URL / sold-out auto-correct) are reflected back into the UI.
 */
class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[role="combobox"]');
    this.listbox = this.querySelector('[role="listbox"]');
    this.hiddenInput = this.querySelector('input[type="hidden"]');
    this.options = Array.from(this.querySelectorAll('[role="option"]'));

    if (!this.trigger || !this.listbox || !this.hiddenInput || !this.options.length) {
      return;
    }

    const labelledby = (this.trigger.getAttribute('aria-labelledby') || '').trim();
    const valueId = labelledby.split(/\s+/).pop();
    this.valueEl = valueId ? this.querySelector('#' + CSS.escape(valueId)) : null;

    this.activeIndex = this.options.findIndex(
      (option) => option.getAttribute('aria-selected') === 'true'
    );
    if (this.activeIndex < 0) this.activeIndex = 0;

    this._onDocumentPointer = this._onDocumentPointer.bind(this);

    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (event) => this._onKeyDown(event));

    // The options are <li> elements, so pressing one would otherwise blur the trigger and the
    // "focus left the component" handler below would close the listbox before the click landed
    // — making the options impossible to pick with a pointer. Keeping the default action means
    // focus never leaves the trigger, which is what the combobox pattern expects anyway.
    this.listbox.addEventListener('mousedown', (event) => event.preventDefault());

    this.options.forEach((option, index) => {
      option.addEventListener('click', () => this.select(index, { close: true, focus: true }));
    });

    // Reflect value changes made by <variant-picker> (URL / sold-out auto-correct).
    this.hiddenInput.addEventListener('change', () => this._onInputChanged());

    // Close the listbox when focus leaves the component entirely (e.g. Tab / Shift+Tab).
    // Focus stays on the trigger while navigating, so this only fires on a real exit.
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget) {
        if (!this.contains(event.relatedTarget)) this.close();
        return;
      }

      // Focus was dropped rather than moved (no relatedTarget). Re-check once the interaction
      // has finished so an in-progress pointer press inside the component is not cut short.
      requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocumentPointer);
  }

  get expanded() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  open() {
    if (this.expanded) return;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.listbox.hidden = false;
    this._setActive(this.activeIndex, { scroll: true });
    document.addEventListener('pointerdown', this._onDocumentPointer);
  }

  close({ focus } = {}) {
    if (!this.expanded) return;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.listbox.hidden = true;
    this.trigger.removeAttribute('aria-activedescendant');
    document.removeEventListener('pointerdown', this._onDocumentPointer);
    if (focus) this.trigger.focus();
  }

  toggle() {
    if (this.expanded) {
      this.close({ focus: true });
    } else {
      this.open();
    }
  }

  _onDocumentPointer(event) {
    if (!this.contains(event.target)) this.close();
  }

  /** Move the highlight + aria-activedescendant to an option (only meaningful while open). */
  _setActive(index, { scroll } = {}) {
    if (index < 0 || index >= this.options.length) return;
    this.activeIndex = index;
    if (!this.expanded) return;
    const option = this.options[index];
    this.trigger.setAttribute('aria-activedescendant', option.id);
    if (scroll) option.scrollIntoView({ block: 'nearest' });
  }

  /** Index of the next non-hidden option in the given direction, wrapping around. */
  _nextSelectable(from, direction) {
    let index = from;
    for (let step = 0; step < this.options.length; step += 1) {
      index += direction;
      if (index < 0) index = this.options.length - 1;
      if (index >= this.options.length) index = 0;
      if (!this.options[index].hidden) return index;
    }
    return from;
  }

  _valueOf(option) {
    return option.dataset.value != null ? option.dataset.value : option.textContent.trim();
  }

  /**
   * Commit a selection. The value follows the highlighted option as the user traverses, so this
   * updates aria-selected, the visible value, aria-activedescendant, and the hidden input
   * (dispatching a bubbling change unless `silent`).
   */
  select(index, { close, focus, silent } = {}) {
    if (index < 0 || index >= this.options.length) return;
    this.activeIndex = index;
    const option = this.options[index];

    this.options.forEach((candidate) => {
      if (candidate === option) {
        candidate.setAttribute('aria-selected', 'true');
      } else {
        candidate.removeAttribute('aria-selected');
      }
    });

    const value = this._valueOf(option);
    if (this.valueEl) this.valueEl.textContent = value;

    if (this.expanded) {
      this.trigger.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    if (this.hiddenInput.value !== value) {
      this.hiddenInput.value = value;
      if (!silent) {
        this._selfChange = true;
        this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (close) this.close({ focus });
  }

  _onInputChanged() {
    // Ignore the change we dispatched ourselves; only react to external updates.
    if (this._selfChange) {
      this._selfChange = false;
      return;
    }
    const index = this.options.findIndex(
      (option) => this._valueOf(option) === this.hiddenInput.value
    );
    if (index >= 0) this.select(index, { silent: true });
  }

  _onKeyDown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.select(this._nextSelectable(this.activeIndex, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.select(this._nextSelectable(this.activeIndex, -1));
        break;
      case 'Home':
        if (this.expanded) {
          event.preventDefault();
          this.select(this._nextSelectable(-1, 1));
        }
        break;
      case 'End':
        if (this.expanded) {
          event.preventDefault();
          this.select(this._nextSelectable(this.options.length, -1));
        }
        break;
      case 'Escape':
        if (this.expanded) {
          event.preventDefault();
          this.close({ focus: true });
        }
        break;
      // Enter / Space toggle the listbox via the button's native click activation.
      default:
        break;
    }
  }
}

if (!window.customElements.get('custom-select')) {
  window.customElements.define('custom-select', CustomSelect);
}
